import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { drw, errText } from "./api";

/** TKO-3 §0 — a re-uploaded Bluebeam export against the set it replaced, per scope: Merge
 *  (default) carries what the new export lacks and never touches app-added or assigned marks;
 *  Replace lets that scope's old Bluebeam marks lapse — refused while any unit of the scope has a
 *  completion record. The preview is saved on the set as the import record. */
export default function ReimportDialog({ setId, onApplied, onClose }) {
  const [prev, setPrev] = useState(null);
  const [choice, setChoice] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { drw.get(`/sets/${setId}/reimport-preview`).then(({ data }) => setPrev(data)).catch((e) => toast.error(errText(e))); }, [setId]);
  const apply = async () => {
    setBusy(true);
    try { const { data } = await drw.post(`/sets/${setId}/reimport-apply`, { choices: choice }); toast.success(`Reconciled ${data.applied.length} scope(s)`); onApplied?.(data); onClose(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-3" data-testid="reimport-dialog">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] overflow-auto p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div><div className="font-semibold">Reconcile the re-uploaded takeoff</div><div className="text-xs text-neutral-500">{prev?.prior_label ? `against ${prev.prior_label}` : ""} · matched within {prev?.tolerance} sheet units</div></div>
          <button type="button" onClick={onClose} className="h-8 px-3 rounded-md border text-sm" data-testid="reimport-close">Close</button>
        </div>
        {!prev && <div className="text-sm text-neutral-500">Comparing…</div>}
        {prev && !prev.prior_set_id && <div className="text-sm text-neutral-600">{prev.note}</div>}
        {prev?.applied && <div className="text-xs px-2 py-1 rounded bg-green-50 text-green-800" data-testid="reimport-applied">Applied {String(prev.applied.at).slice(0, 16)} by {prev.applied.by}</div>}
        {prev?.scopes?.length > 0 && (
          <table className="w-full text-xs" data-testid="reimport-table">
            <thead><tr className="text-left text-neutral-500"><th>Scope</th><th className="text-right">Existing</th><th className="text-right">New export</th><th className="text-right">Matched</th><th className="text-right">New only</th><th className="text-right">Missing from new</th><th className="text-right">App-added</th><th className="text-right">Installed</th><th>Action</th></tr></thead>
            <tbody>
              {prev.scopes.map((r) => (
                <tr key={r.scope_code} className="border-t" data-testid={`reimport-scope-${r.scope_code}`}>
                  <td className="py-1 font-semibold">{r.scope_code}{r.new_scope && <span className="ml-1 px-1 rounded bg-sky-100 text-sky-800">new scope</span>}</td>
                  <td className="text-right">{r.existing}</td><td className="text-right">{r.new_export}</td><td className="text-right">{r.matched}</td>
                  <td className="text-right">{r.new_only}</td><td className="text-right">{r.missing_from_new}</td><td className="text-right">{r.app_added}</td><td className="text-right">{r.installed_units}</td>
                  <td>
                    <select value={choice[r.scope_code] || "merge"} onChange={(e) => setChoice({ ...choice, [r.scope_code]: e.target.value })} disabled={!!prev.applied} className="h-7 border rounded px-1" data-testid={`reimport-choice-${r.scope_code}`}>
                      <option value="merge">Merge</option>
                      <option value="replace" disabled={!r.replace_allowed}>Replace{r.replace_allowed ? "" : ` — ${r.replace_blocked_reason}`}</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {prev?.prior_set_id && !prev.applied && <div className="flex justify-end"><button type="button" onClick={apply} disabled={busy} className="h-8 px-3 rounded-md bg-neutral-900 text-white text-sm" data-testid="reimport-apply">Apply</button></div>}
      </div>
    </div>
  );
}
